<template>
  <main
    :class="bem()"
  >
    <component
      :is="heroComponent"
      :content="{
        fullWidthTitle: article.fullWidthTitle,
        ...article.hero,
        link: categoryLink,
      }"
    />

    <div :class="[bem('content'), 'global-wrapper']">
      <ArticleMeta
        :meta-group-title="article.metaGroupTitle"
        :content="article.metaBlock"
        :class="
          bem('meta', {
            empty: !article.metaBlock || !article.metaBlock.items.length,
            hasDate: !!article.date,
          })
        "
        :date="article.date"
        :title="article.title"
        :description="description"
        :is-pro-team-page="article.isProTeamPage"
        :articles="relatedContent"
      />
      <div :class="bem('body')">
        <ArticleContentLoop :content="article.content" />

        <ArticleFooter
          v-if="!article.isProTeamPage"
          :link="article.categoryLink"
          :title="article.title"
          :description="description"
          :articles="relatedContent"
        />
      </div>
    </div>

    <div :class="[bem('footer'), 'global-wrapper']">
      <div :class="bem('footer-inner')">
        <SecondaryLineLink
          v-if="article.isProTeamPage"
          to="/explore/pro-team"
        >
          Back to Pro Team
        </SecondaryLineLink>
      </div>
    </div>
  </main>
</template>

<script setup>
import { articleGql, articlesByTagIdsGql } from '@/gql/explore/article.gql.js'
import { athleteGql } from '@/gql/explore/athlete.gql.js'
import { modelArticle, modelRelatedArticle } from '@/models/content/article'
import { modelAthlete } from '@/models/content/athlete'
import organisation from '@/assets/js/constants/organisation'
import { HeroArticle, HeroProfile } from '#components'

const route = useRoute()
const bem = useBEM('ArticlePost')

const article = ref()
const relatedArticles = ref(null)
const { $head, $track } = useNuxtApp()

const isAthlete = (route.meta?.category || route.params.category) === 'pro-team'
const gqlQuery = isAthlete ? athleteGql : articleGql

const { data: rawArticle, error: articleResponseError } =
  await useDatoQuery({
    query: gqlQuery,
    variables: {
      slug: route.meta?.article || route.params.article,
    },
  })


if (articleResponseError.value) {
  const { $sentry } = useNuxtApp()
  $sentry.captureException(articleResponseError)
  throw createError({ message: 'Could not load article' })
}

if (!rawArticle.value?.athlete && !rawArticle.value?.article) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Not Found',
    fatal: true
  })
}

if (isAthlete) {
  article.value = modelAthlete(rawArticle.value.athlete)
} else {
  article.value = modelArticle(rawArticle.value.article)
}

const articleTagIds = article.value.tags?.map((tag) => tag)


if (articleTagIds.length) {
  const { data: relatedArticlesResponse } = await useDatoQuery({
    query: articlesByTagIdsGql,
    variables: {
      tagIds: articleTagIds,
      excludeArticleId: article.value.id,
      limit: 3,
    },
  })

  if (relatedArticlesResponse.value) {
    relatedArticles.value = relatedArticlesResponse.value
  }
}

const relatedContent = computed(() => {
  if (!relatedArticles.value) return []

  return relatedArticles.value?.allArticles?.map(modelRelatedArticle)
})

const heroComponent = computed(() => {
  return article.value.isProTeamPage ? HeroProfile : HeroArticle
})

const categoryLink = computed(() => {
  return {
    text: article.value.category?.title,
    slug: article.value.category?.fullSlug,
  }
})

const description = computed(() => {
  return article.value?.meta?.find((meta) =>
    meta?.attributes?.name?.includes('description')
  )?.attributes?.content
})

onMounted(() => {
  if (article.value?.isProTeamPage) {
    $track.explore.viewAthlete(article.value)
  } else {
    $track.explore.viewArticle(article.value)
  }
})

useHead(() => $head(article.value?.meta))

useJsonld({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.value?.title || '',
  image: [article.value?.hero?.media?.image?.src || ''],
  datePublished: `${article.value?.date}T00:00:00+00:00`,
  dateModified: `${article.value?.date}T00:00:00+00:00`,
  keywords: article.value?.tags?.join(', '),
  author: {
    '@type': 'Organization',
    name: 'SunGod',
    url: 'https://www.sungod.co/',
  },
  publisher: organisation
})
</script>

<style lang="postcss">
.ArticlePost__content {
  @apply flex
    flex-col
    lg:flex-row
    lg:justify-between;
}

.ArticlePost__footer {
  @apply overflow-hidden;
}

.ArticlePost__footer-inner {
  @apply flex flex-row
    w-full
    justify-start
    mb-lg sm:mb-xxl;

  margin-top: 20px;

  @screen lg {
    margin-top: 40px;
    margin-left: calc((var(--column-width) * 3) + (var(--inner-gutter) * 3));
  }
}

.ArticlePost__meta {
  @screen lg {
    width: calc((var(--column-width) * 3) + (var(--inner-gutter) * 2));
  }

  @screen lgMax {
    margin-bottom: 60px;
  }

  &--empty {
    @screen lgMax {
      margin-bottom: 0;
    }
  }

  &--has-date.Article__meta--empty {
    @screen lgMax {
      @apply mb-md;
    }
  }
}

.ArticlePost__body {
  @screen lg {
    width: calc((var(--column-width) * 9) + (var(--inner-gutter) * 8));
  }
}
</style>
